import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { BrandingConfig, Theme } from '@/types'

const DEFAULT_BRANDING: BrandingConfig = {
  companyName: '',
  logo: null,
  primaryColor: '#14b8a6',
  secondaryColor: '#10b981',
  accentColor: '#f59e0b',
  fontFamily: 'default',
  theme: 'dark',
}

const STORAGE_KEY = 'wealthspace-branding'

interface BrandingContextValue {
  branding: BrandingConfig
  updateBranding: (updates: Partial<BrandingConfig>) => void
  resetBranding: () => void
  setTheme: (theme: Theme) => void
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined)

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig>(() => {
    // Load from localStorage on init
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          return { ...DEFAULT_BRANDING, ...JSON.parse(stored) }
        } catch {
          // Invalid JSON, use defaults
        }
      }
    }
    return DEFAULT_BRANDING
  })

  // Persist to localStorage when branding changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branding))
  }, [branding])

  const updateBranding = useCallback((updates: Partial<BrandingConfig>) => {
    setBranding((prev) => ({ ...prev, ...updates }))
  }, [])

  const resetBranding = useCallback(() => {
    setBranding(DEFAULT_BRANDING)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const setTheme = useCallback((theme: Theme) => {
    setBranding((prev) => ({ ...prev, theme }))
  }, [])

  return (
    <BrandingContext.Provider value={{ branding, updateBranding, resetBranding, setTheme }}>
      {children}
    </BrandingContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBranding() {
  const context = useContext(BrandingContext)
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}

export { DEFAULT_BRANDING }
